import React, { useState } from "react";
import styled from "styled-components";
import { X } from "lucide-react";
import firebase from "../firebase";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  font-family: "Prompt", sans-serif;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 1;
`;

const ModalTitle = styled.h2`
  font-size: 25px;
  font-weight: 400;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #9ca3af;

  &:hover {
    color: #6b7280;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #686868;
  display: block;
  margin-bottom: 0.5rem;
`;

const RequiredStar = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  color: #686868;
  font-weight: 400;

  &::placeholder {
    color: #cfd3d4;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #cfd3d4;
  border-radius: 6px;
  font-size: 16px;
  color: #686868;
  font-weight: 400;
  font-family: "Prompt", sans-serif;
  resize: vertical;

  &::placeholder {
    color: #cfd3d4;
  }

  &:focus {
    outline: none;
    border-color: #00b5b8;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  color: #cfd3d4;
  font-weight: 400;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00b5b8;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const RadioInput = styled.input`
  accent-color: #00b5b8;
`;

const RadioText = styled.span`
  font-size: 16px;
  color: #686868;
  font-weight: 400;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ImageUploadSection = styled.div`
  margin-bottom: 2rem;
`;

const ImageUploadInput = styled.input`
  display: none;
`;

const ImageUploadButton = styled.button`
  background-color: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  cursor: pointer;
  color: #374151;
  font-weight: 400;
  display: inline-block;
  vertical-align: top;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-left: 1rem;
  vertical-align: top;
`;

const ImagePreview = styled.img`
  max-width: 200px;
  max-height: 200px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: #dc2626;
  }
`;

const MapContainer = styled.div`
  margin-bottom: 1.5rem;
  height: 300px;
  background-color: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
`;

const NoticeText = styled.div`
  margin-bottom: 1.5rem;
  font-size: 14px;
  color: #686868;
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  position: sticky;
  bottom: 0;
  background-color: white;
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: #00b5b8;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.875rem;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background-color: #009b9e;
  }
`;

const CampaignManagementModal = ({
  isOpen,
  onClose,
  onSave,
  allCampaigns = [],
}) => {
  const [formData, setFormData] = useState({
    campaignName: "",
    campaignDescription: "",
    serviceType: "hospital",
    campaignDuration: "",
    startDate: "",
    endDate: "",
    hasCost: false,
    cost: "",
    promoCode: "",
    company: "",
    latitude: "",
    longitude: "",
    deliveryLocation: "",
    address: "",
    postalCode: "",
    subdistrictCode: "",
    note: "",
  });

  const [selectedCampaignImage, setSelectedCampaignImage] = useState(null);
  const [selectedCampaignIcon, setSelectedCampaignIcon] = useState(null);
  const [campaignImageFile, setCampaignImageFile] = useState(null);
  const [campaignIconFile, setCampaignIconFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const FIREBASE_STORAGE_BASE_URL =
    "https://firebasestorage.googleapis.com/v0/b/diet-z.appspot.com/o/";

  const extractRelativePath = (fullURL) => {
    if (!fullURL || fullURL.trim() === "") return "";
    if (fullURL.startsWith(FIREBASE_STORAGE_BASE_URL)) {
      return fullURL.replace(FIREBASE_STORAGE_BASE_URL, "");
    }
    return fullURL;
  };

  const uploadImageToFirebase = async (file, folder = "campaign-images") => {
    if (!file) return null;
    try {
      const timestamp = Date.now();
      const fileExtension = file.name.substring(file.name.lastIndexOf("."));
      const fileName = `${folder}/mfu-${timestamp}${fileExtension}`;
      const storageRef = firebase.storage().ref();
      const fileRef = storageRef.child(fileName);
      await fileRef.put(file);
      const downloadURL = await fileRef.getDownloadURL();
      return downloadURL;
    } catch (error) {
      console.error("Error uploading to Firebase:", error);
      throw error;
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCampaignImageFile(file);
      setSelectedCampaignImage(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setCampaignImageFile(null);
    setSelectedCampaignImage(null);
    const fileInput = document.getElementById("image-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCampaignIconFile(file);
      setSelectedCampaignIcon(URL.createObjectURL(file));
    }
  };

  const handleRemoveIcon = () => {
    setCampaignIconFile(null);
    setSelectedCampaignIcon(null);
    const fileInput = document.getElementById("icon-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!formData.campaignName || formData.campaignName.trim() === "") {
      alert("กรุณาใส่ชื่อบริษัท");
      return;
    }

    const trimmedName = formData.campaignName.trim();
    const isDuplicate = allCampaigns.some(
      (c) =>
        c.name &&
        c.name !== "-" &&
        c.name.trim().toLowerCase() === trimmedName.toLowerCase(),
    );
    if (isDuplicate) {
      alert(`ชื่อบริษัท "${trimmedName}" มีอยู่แล้ว กรุณาใช้ชื่ออื่น`);
      return;
    }

    if (onSave) {
      setIsUploading(true);
      try {
        let imageURL = "";
        let iconURL = "";

        if (campaignImageFile) {
          imageURL = await uploadImageToFirebase(campaignImageFile, "campaign-images");
        }
        if (campaignIconFile) {
          iconURL = await uploadImageToFirebase(campaignIconFile, "campaign-icons");
        }

        const imageRelativePath = extractRelativePath(imageURL);
        const iconRelativePath = extractRelativePath(iconURL);

        const apiData = {
          name: formData.campaignName,
          caption: formData.campaignDescription,
          image: imageRelativePath,
          icon: iconRelativePath,
          promocode: formData.promoCode,
          day: formData.campaignDuration || "0",
          datestart: formData.startDate || "0000-00-00",
          datestop: formData.endDate || "0000-00-00",
          lat: formData.latitude || "0",
          lng: formData.longitude || "0",
          address: formData.address,
          address_name: formData.deliveryLocation,
          address_note: formData.note,
          postcode: formData.postalCode || "0",
          district_code: formData.subdistrictCode || "0",
        };

        onSave(apiData);
        onClose();
      } catch (err) {
        console.error("Error uploading image:", err);
        alert("เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ กรุณาลองใหม่อีกครั้ง");
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>จัดการบริษัท</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormGroup>
            <Label>
              ชื่อบริษัท
              <RequiredStar>*</RequiredStar>
            </Label>
            <Input
              type="text"
              value={formData.campaignName}
              onChange={(e) =>
                handleInputChange("campaignName", e.target.value)
              }
              placeholder="ชื่อบริษัท"
            />
          </FormGroup>

          <FormGroup>
            <Label>แคมเปญส่งเสริมสุขภาพ</Label>
            <TextArea
              value={formData.campaignDescription}
              onChange={(e) =>
                handleInputChange("campaignDescription", e.target.value)
              }
              placeholder="รายละเอียดแคมเปญ"
              rows={3}
            />
          </FormGroup>

          <ImageUploadSection>
            <ImageUploadInput
              type="file"
              id="image-upload"
              accept="image/*"
              onChange={handleImageUpload}
            />
            <ImageUploadButton
              onClick={() => document.getElementById("image-upload").click()}
            >
              เลือกรูปภาพ
            </ImageUploadButton>
            {selectedCampaignImage && (
              <ImagePreviewContainer>
                <ImagePreview
                  src={selectedCampaignImage}
                  alt="Campaign Preview"
                />
                <RemoveImageButton onClick={handleRemoveImage}>
                  ×
                </RemoveImageButton>
              </ImagePreviewContainer>
            )}
          </ImageUploadSection>

          <MapContainer>
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${formData.latitude || "20.031170908430916"},${formData.longitude || "99.87735115782249"}&zoom=15`}
              width="100%"
              height="100%"
              style={{ border: 0, pointerEvents: "auto" }}
              allowFullScreen=""
              loading="lazy"
              title="Google Map Location"
            />
          </MapContainer>

          <NoticeText>
            ระบบจำเป็นต้องขอที่อยู่จัดส่งยาจากท่านเพื่อจัดหาจุดบริการที่ใกล้เคียงกับท่านมากที่สุด
          </NoticeText>

          <GridContainer>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>ละติจูด</Label>
              <Input
                type="text"
                value={formData.latitude}
                onChange={(e) => handleInputChange("latitude", e.target.value)}
                placeholder="20.031170908430916"
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>ลองจิจูด</Label>
              <Input
                type="text"
                value={formData.longitude}
                onChange={(e) => handleInputChange("longitude", e.target.value)}
                placeholder="99.87735115782249"
              />
            </FormGroup>
          </GridContainer>

          <FormGroup>
            <Label>หมายเหตุ</Label>
            <TextArea
              value={formData.note}
              onChange={(e) => handleInputChange("note", e.target.value)}
              placeholder="หมายเหตุ"
              rows={3}
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <SubmitButton onClick={handleSubmit} disabled={isUploading}>
            {isUploading ? "กำลังอัปโหลดรูปภาพ..." : "เพิ่ม"}
          </SubmitButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default CampaignManagementModal;
